import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useVideo } from '../context/VideoContext'
import { VideoGrid } from '../components/Video/VideoGrid'
import { VideoPlayerSimple as VideoPlayer } from '../components/Video/VideoPlayerSimple'
import { Play, Grid, List, Filter, SortAsc } from 'lucide-react'

const HomePage = () => {
  const [user, setUser] = useState(null)
  
  // Load user from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user')
    if (userData) {
      try {
        setUser(JSON.parse(userData))
      } catch (e) {
        console.error('Failed to parse user data:', e)
      }
    }
  }, [])
  const {
    videos,
    loading,
    selectedVideo,
    selectVideo,
    clearSelectedVideo
  } = useVideo()

  console.log('HomePage: videos count:', videos.length)
  console.log('HomePage: selectedVideo:', selectedVideo)
  
  const [searchParams] = useSearchParams()
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('newest')
  const [filteredVideos, setFilteredVideos] = useState([])

  // Categories for filtering
  const categories = [
    'all',
    'Family Events',
    'Holidays', 
    'Birthdays',
    'Vacations',
    'Kids',
    'Pets',
    'Home Videos',
    'Special Occasions',
    'Other'
  ]

  // Sort options
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'title', label: 'Title A-Z' },
    { value: 'duration', label: 'Longest First' }
  ]

  // Filter and search videos
  useEffect(() => {
    let filtered = [...videos]
    
    // Apply search if query parameter exists
    const searchQuery = searchParams.get('search')
    if (searchQuery) {
      filtered = filtered.filter(video => 
        video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(video => 
        video.category === selectedCategory
      )
    }
    
    // Apply sorting
    filtered = sortVideos(filtered, sortBy)
    
    setFilteredVideos(filtered)
  }, [videos, searchParams, selectedCategory, sortBy])

  const sortVideos = (videosToSort, sortBy) => {
    const sorted = [...videosToSort]
    
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      case 'title':
        return sorted.sort((a, b) => a.title.localeCompare(b.title))
      case 'duration':
        return sorted.sort((a, b) => (b.duration || 0) - (a.duration || 0))
      default:
        return sorted
    }
  }

  // Get featured video (most recent or manually selected)
  const featuredVideo = filteredVideos.length > 0 ? filteredVideos[0] : null
  const searchQuery = searchParams.get('search')


  if (loading && videos.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-netflix-black">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-400">Loading your videos...</p>
        </div>
      </div>
    )
  }

  // If no videos and not loading, show empty state
  if (!loading && videos.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-netflix-black">
        <div className="text-center">
          <p className="text-white text-xl">No videos found</p>
          <p className="text-gray-400">Upload your first family video to get started</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" onClick={() => console.log('HomePage container clicked')}>
      {/* Hero Section */}
      {!searchQuery && featuredVideo && (
        <section className="relative h-96 md:h-[500px] bg-gradient-to-r from-netflix-black to-transparent">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${featuredVideo.thumbnail_url || '/placeholder-video-hero.jpg'})`,
              backgroundPosition: 'center 20%'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-netflix-black via-black/50 to-transparent" />
          </div>
          
          <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
            <div className="max-w-lg">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                {featuredVideo.title}
              </h1>
              
              <p className="text-lg text-gray-300 mb-6 line-clamp-3">
                {featuredVideo.description}
              </p>
              
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => selectVideo(featuredVideo)}
                  className="flex items-center space-x-2 bg-white text-black px-6 py-3 rounded font-semibold hover:bg-gray-200 transition-colors"
                >
                  <Play className="w-5 h-5 fill-current" />
                  <span>Play</span>
                </button>
                
                <div className="text-sm text-gray-400">
                  <span className="capitalize">{featuredVideo.category}</span>
                  {featuredVideo.duration && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{Math.floor(featuredVideo.duration / 60)}m</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Controls Bar */}
      <section className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          
          {/* Title and Count */}
          <div>
            <h2 className="text-2xl font-bold text-white">
              {searchQuery ? `Search Results for "${searchQuery}"` : 'Family Videos'}
            </h2>
            <p className="text-gray-400 mt-1">
              {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
              {selectedCategory !== 'all' && ` in ${selectedCategory}`}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-4">
            
            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm focus:border-netflix-red focus:outline-none"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-2">
              <SortAsc className="w-4 h-4 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm focus:border-netflix-red focus:outline-none"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center border border-gray-600 rounded overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-netflix-red text-white' : 'bg-gray-800 text-gray-400'} hover:text-white transition-colors`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-netflix-red text-white' : 'bg-gray-800 text-gray-400'} hover:text-white transition-colors`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Videos Grid/List */}
      <section className="container mx-auto px-4 pb-12">
        {filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📹</div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery ? 'No videos found' : 'No videos yet'}
            </h3>
            <p className="text-gray-400">
              {searchQuery 
                ? 'Try a different search term or browse all videos' 
                : 'Upload your first family video to get started'
              }
            </p>
          </div>
        ) : (
          <VideoGrid 
            videos={filteredVideos}
            viewMode={viewMode}
            loading={loading}
          />
        )}
      </section>

      {/* Video Player Modal */}
      {selectedVideo && (
        <>
          {console.log('HomePage: Rendering VideoPlayer with:', selectedVideo.title)}
          <VideoPlayer
            video={selectedVideo}
            onClose={clearSelectedVideo}
          />
        </>
      )}
    </div>
  )
}

export default HomePage